import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CustomTable from "../components/CustomTable";
import FilterBar from "./components/FilterBar";
import { ticketService } from "../../../services/api/ticketService";
import { formatDate } from "../../../utils/DateFormat";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import TableSkeleton from "../components/TableSkeleton";
import MobilePatternHeader from "../components/MobilePatternHeader";
import { StatusBadge } from "../../../constants/statusConstants";
import Button from "../components/Button";
import { useDebouncedValue } from "../../../hooks/useDebounce";
import EmptyState from "../components/EmptyState";

const listVariants = {
  hidden: { opacity: 0, y: 8 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.16, delay: i * 0.025 },
  }),
};

const daysVN = [
  "Chủ Nhật",
  "Thứ 2",
  "Thứ 3",
  "Thứ 4",
  "Thứ 5",
  "Thứ 6",
  "Thứ 7",
];
const getDayOfWeek = (dateStr) =>
  daysVN[new Date(dateStr).getDay()] || "Không xác định";

const TICKET_STATUS_MAP = {
  pending: "Chờ phản hồi",
  "in-progress": "Đang xử lý",
  resolved: "Đã hoàn thiện",
};

const TableTicket = ({ isMobile }) => {
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false); // tải lần đầu / thay filter
  const [loadingMore, setLoadingMore] = useState(false); // tải thêm
  const [error, setError] = useState("");

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("fromDate") || "");
  const [toDate, setToDate] = useState(searchParams.get("toDate") || "");
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1,
  );
  const [totalPages, setTotalPages] = useState(1);

  const debouncedSearch = useDebouncedValue(search, 300);
  const abortRef = useRef(null);

  const formatTickets = useCallback(
    (tickets, page) =>
      tickets.map((t, index) => ({
        id: itemsPerPage * (page - 1) + index + 1,
        createdAt: t.createdAt,
        date: formatDate(t.createdAt) || "N/A",
        content: t.message || "No content",
        status: t.status || "pending", // chỉ giữ value, render badge ở UI
        nav_id: t._id,
      })),
    [itemsPerPage],
  );

  const fetchTickets = useCallback(
    async ({
      searchVal,
      fromDateVal,
      toDateVal,
      page,
      limit,
      append = false,
      signal,
    }) => {
      try {
        if (!append) {
          setLoading(true);
          setError("");
        }
        const res = await ticketService.get_all(
          searchVal,
          fromDateVal,
          toDateVal,
          page,
          limit,
          token_client,
          signal, // nếu service của bạn nhận signal, pass vào; nếu không thì bỏ
        );
        const tickets = res?.data?.tickets || [];
        const formatted = formatTickets(tickets, page);

        setData((prev) => (append ? [...prev, ...formatted] : formatted));
        setTotalPages(res?.data?.totalPages || 1);
        setCurrentPage(Number(res?.data?.currentPage) || page);
      } catch (err) {
        if (err?.name === "AbortError") return; // bị hủy -> bỏ qua
        console.error(err);
        toast.dismiss();
        setError("Lỗi khi lấy dữ liệu từ server");
        toast.error("Lỗi khi lấy dữ liệu từ server");
        setData((prev) => (append ? prev : []));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [token_client, formatTickets],
  );

  const applySearchParams = useCallback(
    (next = {}) => {
      const params = new URLSearchParams();
      const s = next.search ?? debouncedSearch;
      const f = next.fromDate ?? fromDate;
      const t = next.toDate ?? toDate;
      const p = next.page ?? 1;
      if (s) params.set("search", s.trim());
      if (f) params.set("fromDate", f);
      if (t) params.set("toDate", t);
      params.set("page", String(p));
      setSearchParams(params, { replace: false });
    },
    [debouncedSearch, fromDate, toDate, setSearchParams],
  );

  const handleSearchClick = () => applySearchParams({ page: 1 });

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearchClick();
  };

  useEffect(() => {
    const s = searchParams.get("search") || "";
    const f = searchParams.get("fromDate") || "";
    const t = searchParams.get("toDate") || "";
    const p = Number(searchParams.get("page")) || 1;

    setSearch(s);
    setFromDate(f);
    setToDate(t);

    // Hủy request cũ
    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    fetchTickets({
      searchVal: s,
      fromDateVal: f,
      toDateVal: t,
      page: p,
      limit: itemsPerPage,
      append: false,
      signal: controller.signal,
    });

    // cleanup
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, itemsPerPage, fetchTickets]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!isMobile) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          !loading &&
          !loadingMore &&
          currentPage < totalPages
        ) {
          const nextPage = currentPage + 1;
          setLoadingMore(true);

          // Hủy request cũ (append giữ cũ nên không abort fetch trước)
          const controller = new AbortController();
          fetchTickets({
            searchVal: search,
            fromDateVal: fromDate,
            toDateVal: toDate,
            page: nextPage,
            limit: itemsPerPage,
            append: true,
            signal: controller.signal,
          }).then(() => {
            setCurrentPage(nextPage);
          });
        }
      },
      { rootMargin: "120px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [
    isMobile,
    loading,
    loadingMore,
    currentPage,
    totalPages,
    fetchTickets,
    search,
    fromDate,
    toDate,
    itemsPerPage,
  ]);

  const columns = useMemo(
    () => [
      {
        key: "id",
        title: "STT",
        minWidth: "20px",
        align: "text-left",
        render: (value) => <div className="pl-2">{value}</div>,
      },
      { key: "date", title: "Ngày", minWidth: "120px" },
      {
        key: "content",
        title: "Nội dung",
        minWidth: "600px",
        render: (value) => (
          <div className="min-w-[600px] truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        key: "status",
        title: "Trạng thái",
        align: "text-center",
        minWidth: "150px",
        render: (value) => {
          const label = TICKET_STATUS_MAP[value] || value;

          return (
            <div className="flex items-center justify-center">
              <StatusBadge status={label} />
            </div>
          );
        },
      },
    ],
    [],
  );
  const SkeletonItem = ({ className = "" }) => (
    <div
      className={`animate-pulse bg-white border border-[#EAEAEA] ${className} rounded-lg px-5 py-[13.5px]`}
    >
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <div className="w-5 h-5 bg-gray-200 rounded" />
          <div className="h-4 w-48 bg-gray-200 rounded" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );

  /* =========== Loading lần đầu =========== */
  if (loading && !loadingMore && data.length === 0) {
    return isMobile ? (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Danh sách yêu cầu"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-64px)] bg-[#F2F2F2] px-3 pt-2">
          {/* Search */}
          <div className="relative w-full mb-2">
            <input
              type="text"
              placeholder="Bạn đang muốn tìm kiếm..."
              className="border border-gray-200 rounded-3xl px-4 py-2 focus:outline-blue-500 w-full"
              value={search}
              autoComplete="off"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              aria-label="Tìm kiếm"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
              onClick={handleSearchClick}
            >
              <img src="/icons/search-black.svg" alt="" />
            </button>
          </div>

          {/* List */}
          <SkeletonItem />
        </div>
      </div>
    ) : (
      <>
        <FilterBar
          search={search}
          setSearch={setSearch}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleSearch={handleSearchClick}
        />{" "}
        <TableSkeleton />;
      </>
    );
  }

  /* =========== RENDER =========== */
  return isMobile ? (
    /* ================== MOBILE ================== */
    <div className="h-full w-full">
      <MobilePatternHeader
        title="Danh sách yêu cầu"
        onBack={() => navigate(-1)}
      />

      <div className="h-[calc(100%-64px)] bg-[#F2F2F2] px-3 pt-2">
        {/* Search */}
        <div className="relative w-full mb-2">
          <input
            type="text"
            placeholder="Bạn đang muốn tìm kiếm..."
            className="border border-gray-200 rounded-3xl px-4 py-2 focus:outline-blue-500 w-full"
            value={search}
            autoComplete="off"
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            aria-label="Tìm kiếm"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
            onClick={handleSearchClick}
          >
            <img src="/icons/search-black.svg" alt="" />
          </button>
        </div>

        {/* List */}
        <div className="h-[calc(100%-190px)] overflow-auto pr-1">
          {data.length > 0 ? (
            <div className="pb-6">
              <AnimatePresence initial={false}>
                {data.map((ticket, i) => (
                  <motion.div
                    key={ticket.nav_id}
                    custom={i}
                    initial="hidden"
                    animate="show"
                    exit={{ opacity: 0, scale: 0.98 }}
                    variants={listVariants}
                    className="w-full bg-white rounded-lg p-3 mb-3 border border-[#EAEAEA] cursor-pointer"
                    onClick={() =>
                      navigate(`/danh-sach-yeu-cau/ticket/${ticket.nav_id}`)
                    }
                  >
                    <div className="flex justify-between items-center mb-2 text-sm text-gray-700 font-medium">
                      <span className="font-sf-semibold">
                        {getDayOfWeek(ticket.createdAt)}, ngày {ticket.date}
                      </span>
                      <StatusBadge
                        status={
                          TICKET_STATUS_MAP[ticket.status] || ticket.status
                        }
                      />
                    </div>
                    <p className="text-sm line-clamp-3">{ticket.content}</p>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Loading more / end */}
              {loadingMore && (
                <div className="text-center py-3 text-sm text-gray-500">
                  Đang tải thêm...
                </div>
              )}
              {!loadingMore && currentPage >= totalPages && data.length > 0 && (
                <div className="text-center py-3 text-sm text-gray-400">
                  Không còn dữ liệu nào.
                </div>
              )}
              {/* Sentinel cho IntersectionObserver */}
              <div ref={sentinelRef} className="h-1" />
            </div>
          ) : error ? (
            <EmptyState
              image={null}
              description="Có lỗi khi tải dữ liệu."
              className="h-[50vh] py-0"
            />
          ) : (
            <EmptyState
              image={null}
              description="Không có yêu cầu nào."
              className="h-[50vh] py-0"
            />
          )}
        </div>
      </div>

      {/* Floating action */}
      <div className="w-full p-3 fixed bottom-[88px] left-0">
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() => navigate("/danh-sach-yeu-cau/them-moi")}
          className="w-full"
        >
          Tạo mới
        </Button>
      </div>
    </div>
  ) : (
    /* ================== DESKTOP (fit khít, scroll trong table) ================== */
    <div className="h-full min-h-0 flex flex-col">
      {/* FilterBar không scroll */}
      <div className="shrink-0">
        <FilterBar
          search={search}
          setSearch={setSearch}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleSearch={handleSearchClick}
        />
      </div>

      {/* Table scroll area */}
      <div className="flex-1 min-h-0 overflow-auto">
        <CustomTable
          columns={columns}
          data={data}
          currentPage={currentPage}
          setCurrentPage={(page) =>
            setSearchParams((prev) => {
              const params = new URLSearchParams(prev);
              params.set("page", page.toString());
              return params;
            })
          }
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          setView
          setEdit={false}
          basePath="/danh-sach-yeu-cau/ticket"
        />
      </div>
    </div>
  );
};

export default TableTicket;
